import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken, clearToken } from "../lib/api";

const AppCtx = createContext(null);

const CART_KEY = "vdc_cart";

export const AppProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    const [cart, setCart] = useState(() => {
        try { return JSON.parse(localStorage.getItem(CART_KEY) || "[]"); } catch { return []; }
    });
    const [cartOpen, setCartOpen] = useState(false);
    const [homepage, setHomepage] = useState(null);

    // persist cart
    useEffect(() => {
        localStorage.setItem(CART_KEY, JSON.stringify(cart));
    }, [cart]);

    const refreshUser = useCallback(async (force = false) => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        if (window.location.hash?.includes("session_id=")) {
            setAuthLoading(false);
            return;
        }
        // Skip probe entirely if user has never authenticated (no token) — avoids 401 noise on public browsing
        if (!force && !getToken()) {
            setAuthLoading(false);
            return;
        }
        try {
            const r = await api.get("/auth/me");
            setUser(r.data);
        } catch (e) {
            setUser(null);
        } finally {
            setAuthLoading(false);
        }
    }, []);

    useEffect(() => { refreshUser(); }, [refreshUser]);

    const loadHomepage = useCallback(async () => {
        const r = await api.get("/homepage");
        setHomepage(r.data);
    }, []);
    useEffect(() => { loadHomepage(); }, [loadHomepage]);

    // Auth methods
    const login = async (email, password) => {
        const r = await api.post("/auth/login", { email, password });
        setToken(r.data.token);
        setUser(r.data.user);
        return r.data.user;
    };
    const register = async (email, password, name) => {
        const r = await api.post("/auth/register", { email, password, name });
        setToken(r.data.token);
        setUser(r.data.user);
        return r.data.user;
    };
    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        clearToken();
        setUser(null);
    };

    // Cart methods
    const addToCart = (product, size, quantity = 1) => {
        setCart(prev => {
            const key = `${product.id}-${size}`;
            const existing = prev.find(i => `${i.product_id}-${i.size}` === key);
            if (existing) {
                return prev.map(i => `${i.product_id}-${i.size}` === key ? { ...i, quantity: i.quantity + quantity } : i);
            }
            const price = product.on_sale && product.sale_price ? product.sale_price : product.price;
            return [...prev, {
                product_id: product.id,
                title: product.title,
                brand: product.brand,
                slug: product.slug,
                size,
                price,
                image: product.images?.[0]?.url,
                quantity,
            }];
        });
        setCartOpen(true);
    };
    const updateQty = (idx, qty) => {
        setCart(prev => prev.map((it, i) => i === idx ? { ...it, quantity: Math.max(1, qty) } : it));
    };
    const removeFromCart = (idx) => setCart(prev => prev.filter((_, i) => i !== idx));
    const clearCart = () => setCart([]);

    const cartSubtotal = cart.reduce((s, it) => s + it.price * it.quantity, 0);
    const cartCount = cart.reduce((s, it) => s + it.quantity, 0);

    const value = {
        user, setUser, authLoading, login, register, logout, refreshUser,
        homepage, setHomepage, loadHomepage,
        cart, cartOpen, setCartOpen, addToCart, updateQty, removeFromCart, clearCart, cartSubtotal, cartCount,
    };
    return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
};

export const useApp = () => useContext(AppCtx);
